import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Chip, Grid, TextField, createFilterOptions } from '@mui/material';
import { KeyboardArrowDown as DropdownIcon } from '@mui/icons-material';
import CustomLabel from './CustomLabel';
import { FetchAllCountries, FetchAllStates, FetchAllCities } from 'api/utils';

// Case-insensitive "contains" filter on the option label.
const geoFilter = createFilterOptions({
  matchFrom: 'any',
  trim: true,
  stringify: (o) => o?.label || '',
});

// Figma spec: #E5E7EB border, 8px radius, #6B7380 label/icon color.
const DEFAULT_LABEL_SX = { color: '#6B7380' };
const DEFAULT_INPUT_SX = {
  '& .MuiOutlinedInput-root': { borderRadius: '8px' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB !important' },
};
const DEFAULT_POPUP_ICON = <DropdownIcon sx={{ fontSize: 20, color: '#6B7380' }} />;

// Alphabetical sort by label.
const byLabel = (a, b) => String(a?.label || '').localeCompare(String(b?.label || ''));

/**
 * Reusable Country / State / City multi-select with cascade + upward auto-fill.
 *
 * Controlled component. Values are arrays of Mongo `_id` strings.
 *   props:
 *     countryIds, stateIds, cityIds : string[]
 *     onChange({ countryIds, stateIds, cityIds })
 *
 * Uses raw MUI Autocomplete (NOT CustomAutoComplete) so it does NOT depend on a
 * Formik context — works in both Formik forms and plain useState screens.
 *
 * Cascade: states shown for selected countries, cities for selected states.
 * Auto-fill: selecting a state adds its country; selecting a city adds its state + country.
 *
 * Fetch hooks (api/utils) take the *external* `.value` id as the parent key,
 * while selections are tracked by Mongo `_id`, so we map between the two internally.
 */

const StatesLoader = ({ countryExternalId, onLoaded }) => {
  const { data = [] } = FetchAllStates({ countryID: countryExternalId });
  const key = JSON.stringify(data);
  useEffect(() => {
    onLoaded(countryExternalId, data || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryExternalId, key]);
  return null;
};

const CitiesLoader = ({ stateExternalId, onLoaded }) => {
  const { data = [] } = FetchAllCities({ stateID: stateExternalId });
  const key = JSON.stringify(data);
  useEffect(() => {
    onLoaded(stateExternalId, data || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateExternalId, key]);
  return null;
};

const GeoMultiSelect = ({
  countryIds = [],
  stateIds = [],
  cityIds = [],
  onChange = () => {},
  labels = {
    country: 'PREFERRED COUNTRIES (MULTI SELECT)',
    state: 'PREFERRED STATES (MULTI SELECT)',
    city: 'PREFERRED CITIES (MULTI SELECT)',
  },
  labelSx = DEFAULT_LABEL_SX,
  inputSx = DEFAULT_INPUT_SX,
  popupIcon = DEFAULT_POPUP_ICON,
}) => {
  const { data: countryOptions = [] } = FetchAllCountries({});

  const [statesByCountry, setStatesByCountry] = useState({});
  const [citiesByState, setCitiesByState] = useState({});

  const countryById = useMemo(() => {
    const m = {};
    (countryOptions || []).forEach((c) => { m[c._id] = c; });
    return m;
  }, [countryOptions]);

  const stateOptionsAll = useMemo(() => {
    const all = [];
    const seen = new Set();
    Object.values(statesByCountry).forEach((list) => (list || []).forEach((s) => {
      if (!seen.has(s._id)) { seen.add(s._id); all.push(s); }
    }));
    return all;
  }, [statesByCountry]);

  const stateById = useMemo(() => {
    const m = {};
    stateOptionsAll.forEach((s) => { m[s._id] = s; });
    return m;
  }, [stateOptionsAll]);

  const cityOptionsAll = useMemo(() => {
    const all = [];
    const seen = new Set();
    Object.values(citiesByState).forEach((list) => (list || []).forEach((c) => {
      if (!seen.has(c._id)) { seen.add(c._id); all.push(c); }
    }));
    return all;
  }, [citiesByState]);

  const selectedCountryExternalIds = useMemo(
    () => (countryIds || []).map((id) => countryById[id]?.value).filter(Boolean),
    [countryIds, countryById]
  );
  const selectedStateExternalIds = useMemo(
    () => (stateIds || []).map((id) => stateById[id]?.value).filter(Boolean),
    [stateIds, stateById]
  );

  const handleStatesLoaded = (countryExternalId, list) => {
    setStatesByCountry((prev) => {
      if (JSON.stringify(prev[countryExternalId]) === JSON.stringify(list)) return prev;
      return { ...prev, [countryExternalId]: list };
    });
  };
  const handleCitiesLoaded = (stateExternalId, list) => {
    setCitiesByState((prev) => {
      if (JSON.stringify(prev[stateExternalId]) === JSON.stringify(list)) return prev;
      return { ...prev, [stateExternalId]: list };
    });
  };

  const asOpt = (item) => ({ label: item?.label, value: item?._id });
  const countryValue = (countryOptions || []).filter((c) => countryIds?.includes(c._id)).map(asOpt);
  const stateValue = stateOptionsAll.filter((s) => stateIds?.includes(s._id)).map(asOpt);
  const cityValue = cityOptionsAll.filter((c) => cityIds?.includes(c._id)).map(asOpt);

  const countryOpts = (countryOptions || []).filter((c) => !countryIds?.includes(c._id)).map(asOpt).sort(byLabel);
  const stateOpts = stateOptionsAll.filter((s) => !stateIds?.includes(s._id)).map(asOpt).sort(byLabel);
  const cityOpts = cityOptionsAll.filter((c) => !cityIds?.includes(c._id)).map(asOpt).sort(byLabel);

  const emit = (next) => onChange({
    countryIds: next.countryIds ?? countryIds,
    stateIds: next.stateIds ?? stateIds,
    cityIds: next.cityIds ?? cityIds,
  });

  const handleCountryChange = (event, value) => {
    const newCountryIds = (value || []).map((o) => o.value);
    const keptStateIds = (stateIds || []).filter((sid) => {
      const cid = stateById[sid]?.country_id;
      return !cid || newCountryIds.includes(String(cid));
    });
    const keptCityIds = (cityIds || []).filter((ciid) => {
      const cc = cityOptionsAll.find((c) => c._id === ciid);
      return !cc?.country_id || newCountryIds.includes(String(cc.country_id));
    });
    emit({ countryIds: newCountryIds, stateIds: keptStateIds, cityIds: keptCityIds });
  };

  const handleStateChange = (event, value) => {
    const newStateIds = (value || []).map((o) => o.value);
    const countrySet = new Set(countryIds || []);
    newStateIds.forEach((sid) => {
      const cid = stateById[sid]?.country_id;
      if (cid) countrySet.add(String(cid));
    });
    const keptCityIds = (cityIds || []).filter((ciid) => {
      const cc = cityOptionsAll.find((c) => c._id === ciid);
      return !cc?.state_id || newStateIds.includes(String(cc.state_id));
    });
    emit({ countryIds: Array.from(countrySet), stateIds: newStateIds, cityIds: keptCityIds });
  };

  const handleCityChange = (event, value) => {
    const newCityIds = (value || []).map((o) => o.value);
    const countrySet = new Set(countryIds || []);
    const stateSet = new Set(stateIds || []);
    newCityIds.forEach((ciid) => {
      const cc = cityOptionsAll.find((c) => c._id === ciid);
      if (cc?.state_id) stateSet.add(String(cc.state_id));
      if (cc?.country_id) countrySet.add(String(cc.country_id));
    });
    emit({ countryIds: Array.from(countrySet), stateIds: Array.from(stateSet), cityIds: newCityIds });
  };

  const acProps = (options, value, onChangeHandler, placeholder) => ({
    multiple: true,
    fullWidth: true,
    autoHighlight: true,
    popupIcon,
    options,
    value,
    onChange: onChangeHandler,
    filterOptions: geoFilter,
    isOptionEqualToValue: (o, v) => o?.value === v?.value,
    getOptionLabel: (o) => o?.label || '',
    renderTags: (selected, getTagProps) =>
      selected.map((opt, i) => <Chip key={opt.value} label={opt.label} {...getTagProps({ index: i })} />),
    renderInput: (params) => (
      <TextField {...params} variant="outlined" placeholder={placeholder} InputProps={{ ...params.InputProps }} sx={inputSx} />
    ),
  });

  return (
    <>
      {selectedCountryExternalIds.map((extId) => (
        <StatesLoader key={`sl-${extId}`} countryExternalId={extId} onLoaded={handleStatesLoaded} />
      ))}
      {selectedStateExternalIds.map((extId) => (
        <CitiesLoader key={`cl-${extId}`} stateExternalId={extId} onLoaded={handleCitiesLoaded} />
      ))}

      <Grid item xs={12}>
        <CustomLabel sx={labelSx}>{labels.country}</CustomLabel>
        <Autocomplete {...acProps(countryOpts, countryValue, handleCountryChange, 'Select countries')} />
      </Grid>

      {countryIds?.length > 0 && (
        <Grid item xs={12}>
          <CustomLabel sx={labelSx}>{labels.state}</CustomLabel>
          <Autocomplete {...acProps(stateOpts, stateValue, handleStateChange, 'Select states (optional)')} />
        </Grid>
      )}

      {stateIds?.length > 0 && (
        <Grid item xs={12}>
          <CustomLabel sx={labelSx}>{labels.city}</CustomLabel>
          <Autocomplete {...acProps(cityOpts, cityValue, handleCityChange, 'Select cities (optional)')} />
        </Grid>
      )}
    </>
  );
};

export default GeoMultiSelect;
